import { useMemo, useState } from "react";
import { ZoomIn } from "lucide-react";
import { PHOTOS, CATEGORIES } from "../data/photos";
import Lightbox from "./Lightbox";

export default function Portfolio() {
  const [filter, setFilter] = useState("todas");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filtered = useMemo(
    () =>
      filter === "todas"
        ? PHOTOS
        : PHOTOS.filter((p) => p.category === filter),
    [filter]
  );

  const open = (i) => setLightboxIndex(i);
  const close = () => setLightboxIndex(null);
  const prev = () =>
    setLightboxIndex((i) => (i - 1 + filtered.length) % filtered.length);
  const next = () => setLightboxIndex((i) => (i + 1) % filtered.length);

  return (
    <section
      id="trabajos"
      data-testid="portfolio-section"
      className="relative bg-[var(--fb-bg)] py-20 md:py-32"
    >
      <div className="fb-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="reveal">
            <span className="eyebrow">Trabajos</span>
            <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl text-[var(--fb-ink)] font-medium tracking-tight balance leading-[1.02]">
              Obras que hablan
              <br className="hidden sm:block" /> por nosotros.
            </h2>
          </div>
          <p
            className="text-[var(--fb-ink-soft)] text-base md:text-[17px] max-w-md pretty reveal"
            style={{ transitionDelay: "120ms" }}
          >
            Una selección de proyectos terminados y en proceso. Tocá cualquier
            imagen para verla en grande.
          </p>
        </div>

        {/* Filters */}
        <div
          className="mt-10 flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-2 reveal"
          data-testid="portfolio-filters"
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}-btn`}
              onClick={() => setFilter(c.id)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                filter === c.id
                  ? "bg-[var(--fb-ink)] text-white border-[var(--fb-ink)]"
                  : "bg-transparent text-[var(--fb-ink)] border-[var(--fb-line)] hover:border-[var(--fb-ink)]"
              }`}
            >
              {c.label}
              <span className="ml-2 text-[10px] tracking-[0.18em] opacity-60">
                {c.id === "todas"
                  ? PHOTOS.length
                  : PHOTOS.filter((p) => p.category === c.id).length}
              </span>
            </button>
          ))}
        </div>

        {/* Masonry gallery using CSS columns */}
        <div
          data-testid="portfolio-gallery"
          className="mt-8 columns-1 sm:columns-2 lg:columns-3 gap-3 md:gap-4 [column-fill:_balance]"
        >
          {filtered.map((p, i) => (
            <button
              key={p.id}
              data-testid={`gallery-item-${p.id}`}
              onClick={() => open(i)}
              className="mb-3 md:mb-4 w-full block break-inside-avoid img-card reveal text-left"
              style={{ transitionDelay: `${(i % 9) * 40}ms` }}
              aria-label={`Ver imagen: ${p.alt}`}
            >
              <picture>
                <source srcSet={p.webp} type="image/webp" />
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  decoding="async"
                  width={p.w}
                  height={p.h}
                  style={{ aspectRatio: `${p.w} / ${p.h}` }}
                />
              </picture>
              <span className="cat-tag">
                {CATEGORIES.find((c) => c.id === p.category)?.label}
              </span>
              <span className="absolute top-3 right-3 z-[2] h-9 w-9 rounded-full bg-white/95 text-[var(--fb-ink)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="h-4 w-4" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          photos={filtered}
          index={lightboxIndex}
          onClose={close}
          onPrev={prev}
          onNext={next}
        />
      )}
    </section>
  );
}
